import React, { ChangeEvent, useEffect, useState } from "react";

import ActionButtons from "components/basic/action-buttons/ActionButtons";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { DataTableProps } from "primereact/datatable";
import { DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import { Dialog } from "primereact/dialog";
import { ColumnBodyOptions } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { ScadaPolygon, ScadaPolygonPoint, ScadaProps } from "../scadaTypes";

type ScadaPolygonTableProps = {
  currentConfig: ScadaProps;
  setCurrentConfig: React.Dispatch<React.SetStateAction<ScadaProps>>;
  onConfigChange: (config: ScadaProps) => void;
};

const ScadaPolygonTable: React.FC<ScadaPolygonTableProps> = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange,
}) => {
  const [polygons, setPolygons] = useState<ScadaPolygon[]>(() => currentConfig.polygons ?? []);
  const [openDialog, setOpenDialog] = useState(false);
  const [polygonId, setPolygonId] = useState<string | null>(null);

  useEffect(() => {
    setCurrentConfig({ ...currentConfig, polygons });
    onConfigChange({ ...currentConfig, polygons });
  }, [polygons]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement> | DropdownChangeEvent,
    polygon: ScadaPolygon
  ) => {
    const { name, value } = e.target;
    setPolygons(prev => prev.map(p => (p.id === polygon.id ? { ...p, [name]: value } : p)));
  };

  const renderBgColorField = (data: ScadaPolygon) => (
    <ColorPicker value={data.color} onValueChange={e => handleChange(e, data)} name="color" />
  );

  const renderTitleInput = (data: ScadaPolygon) => (
    <InputText value={data.title} onChange={e => handleChange(e, data)} name="title" />
  );

  const renderRedirectInput = (data: ScadaPolygon) => (
    <InputText
      value={data.redirectTo}
      onChange={e => handleChange(e, data)}
      name="redirectTo"
      placeholder="/dashboard/0/tab/0"
    />
  );

  const renderPoints = (data: ScadaPolygon) => (
    <Button
      label={data.points?.length.toString()}
      icon="pi pi-circle-fill"
      text
      iconPos="right"
      size="small"
      className="w-min"
      onClick={() => {
        setOpenDialog(true);
        setPolygonId(data.id);
      }}
    />
  );

  const renderActions = (rowData: ScadaPolygon) => (
    <ActionButtons
      onClickDelete={() => setPolygons(prev => prev.filter(p => p.id !== rowData.id))}
    />
  );

  const columns: TableColumn<ScadaPolygon>[] = [
    {
      field: "title",
      columnKey: "title",
      header: T("word.title"),
      body: renderTitleInput,
    },
    {
      field: "color",
      columnKey: "color",
      header: T("word.color"),
      body: renderBgColorField,
      className: "w-10rem",
    },
    {
      field: "redirectTo",
      columnKey: "redirectTo",
      header: `${T("action.redirect")} (# ${T("word.route").toLowerCase()})`,
      body: renderRedirectInput,
    },
    {
      field: "points",
      columnKey: "points",
      header: T("word.points"),
      body: renderPoints,
    },
    {
      columnKey: "actions",
      header: T("word.actions"),
      body: renderActions,
    },
  ];

  const tableConfig: DataTableProps<ScadaPolygon[]> = {
    header: (
      <div className="flex justify-content-end flex-wrap gap-2">
        <Button
          label={T("action.create-new")}
          icon="pi pi-plus"
          size="small"
          className="component-editor-button icon-green w-max"
          onClick={() =>
            setPolygons([
              ...polygons,
              { id: v4(), title: "", color: "#2997FF", points: [], redirectTo: "" },
            ])
          }
        />
      </div>
    ),
    paginator: false,
  };

  return (
    <>
      <DataTable columns={columns} data={polygons} tableConfig={tableConfig} />
      <Dialog
        visible={openDialog}
        header={T("word.points")}
        onHide={() => setOpenDialog(false)}
        style={{ width: "min(100%, 500px)" }}
      >
        <PointsTable polygon={polygons.find(p => p.id === polygonId)!} setPolygons={setPolygons} />
      </Dialog>
    </>
  );
};

export default ScadaPolygonTable;

type PointsTableProps = {
  polygon: ScadaPolygon;
  setPolygons: React.Dispatch<React.SetStateAction<ScadaPolygon[]>>;
};
const PointsTable: React.FC<PointsTableProps> = ({ polygon, setPolygons }) => {

  const renderCoordinate = (data: ScadaPolygonPoint, options: ColumnBodyOptions) => (
    <InputNumber 
      value={data[options.field as "x" | "y"]}
      onValueChange={e => {
        setPolygons(prev =>
          prev.map(p =>
            p.id === polygon.id
              ? {
                  ...p,
                  points: p.points.map(point =>
                    point.id === data.id ? { ...point, [options.field!]: e.value } : point
                  ),
                }
              : p
          )
        );
      }}
      name={options.field!}
    />
  )

  const renderActions = (rowData: ScadaPolygonPoint) => (
    <ActionButtons
      onClickDelete={() =>
        setPolygons(prev =>
          prev.map(p =>
            p.id === polygon.id
              ? {
                  ...p,
                  points: p.points.filter(point => point.id !== rowData.id),
                }
              : p
          )
        )
      }
    />
  );

  const columns: TableColumn<ScadaPolygonPoint>[] = [
    {
      field: "x",
      columnKey: "x",
      header: "X, %",
      body: renderCoordinate,
    },
    {
      field: "y",
      columnKey: "y",
      header: "Y, %",
      body: renderCoordinate,
    },
    {
      columnKey: "actions",
      header: T("word.actions"),
      body: renderActions,
    },
  ];

  const tableConfig: DataTableProps<ScadaPolygonPoint[]> = {
    header: (
      <div className="flex justify-content-end flex-wrap gap-2">
        <Button
          label={T("action.add")}
          icon="pi pi-plus"
          size="small"
          className="component-editor-button icon-green w-max"
          onClick={() =>
            setPolygons(prev =>
              prev.map(p =>
                p.id === polygon.id ? { ...p, points: [...p.points, { id: v4(), x: 0, y: 0 }] } : p
              )
            )
          }
        />
      </div>
    ),
    paginator: false,
  };

  return <DataTable columns={columns} data={polygon.points} tableConfig={tableConfig} />;
};
